import { fetchExchangeRates } from '@/integrations/api/currency/client';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { convertWithRates } from '@/lib/currencyConversion/crossRate';


export function useExchangeRates(baseCurrency: string = 'USD') {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['exchangeRates', baseCurrency],
    queryFn: () => fetchExchangeRates(baseCurrency),
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 2 * 60 * 60 * 1000, // 2 hours (formerly cacheTime)
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Convert amount from one currency to another (to the base currency when no target is given)
  const convert = useCallback(
    (amount: number, fromCurrency: string, toCurrency?: string): number =>
      convertWithRates(amount, fromCurrency, toCurrency || baseCurrency, data?.rates),
    [data, baseCurrency]
  );

  // Get exchange rate between two currencies
  const getRate = useCallback(
    (fromCurrency: string, toCurrency?: string): number | null => {
      const targetCurrency = toCurrency || baseCurrency;

      if (fromCurrency === targetCurrency) {
        return 1;
      }

      if (!data?.rates) {
        return null;
      }

      if (!data.rates[fromCurrency] || !data.rates[targetCurrency]) {
        return null;
      }

      return data.rates[targetCurrency] / data.rates[fromCurrency];
    },
    [data, baseCurrency]
  );

  // Check if a currency is supported
  const isSupported = useCallback(
    (currency: string): boolean => {
      return !!data?.rates?.[currency];
    },
    [data]
  );

  return {
    rates: data,
    isLoading,
    error: error as Error | null,
    convert,
    getRate,
    isSupported,
    refetch,
  };
}
